import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/objectWithoutPropertiesLoose";
const _excluded = ["domain"];
import { parseDomain } from '@wix/domains-serverless-interface';
import { DomainsApiClient } from '@wix/domains-serverless-interface/react-kit';
import { createApiClient, createQueriesConfig, QueryConfigTypes } from '@wix/premium-fed-sdk/@apis-infra-react';
import { getPremiumEssentialsSafe } from '@wix/premium-fed-sdk/@premium-essentials';
import { RecommendationFeature } from '@wix/ambassador-premium-domains-v1-recommendation/types';
import { ExperimentSpecs } from '../../constants';
import { selectAlternativesFromRecommendations } from './TldAlternativesWithPrice.utils';
const TLD_POOL_LIMIT = 10;
const AVAILABILITY_PAGE_SIZE = 4;
const MIN_RESULTS = 3;
const MAX_RESULTS = 3;
const RECOMMENDATIONS_OVER_FETCH_FACTOR = 4;
const isReuseRecommendationsOn = () => {
  var _getPremiumEssentials;
  return Boolean((_getPremiumEssentials = getPremiumEssentialsSafe()) == null || (_getPremiumEssentials = _getPremiumEssentials.experiments) == null ? void 0 : _getPremiumEssentials[ExperimentSpecs.BngTldAlternativesFromRecommendations]);
};
const listAlternativesFromRecommendations = async (httpClient, heroDomain, heroSld, maxResults) => {
  const _DomainsApiClient$que = DomainsApiClient.queries({
      httpClient
    }),
    listRecommendations = _DomainsApiClient$que.listRecommendations;
  const _await$listRecommenda = await listRecommendations({
      query: heroSld,
      tlds: [],
      features: [RecommendationFeature.PRICING],
      paging: {
        limit: maxResults * RECOMMENDATIONS_OVER_FETCH_FACTOR
      }
    }),
    _await$listRecommenda2 = _await$listRecommenda.recommendations,
    recommendations = _await$listRecommenda2 === void 0 ? [] : _await$listRecommenda2;
  const alternatives = selectAlternativesFromRecommendations(recommendations, heroDomain, heroSld, maxResults, domain => parseDomain(domain).sld);
  return {
    alternatives
  };
};
const apiConfig = createQueriesConfig({
  tldAlternatives: {
    queryFn: async _ref => {
      let httpClient = _ref.httpClient,
        _ref$request = _ref.request,
        heroDomain = _ref$request.heroDomain,
        _ref$request$maxResul = _ref$request.maxResults,
        maxResults = _ref$request$maxResul === void 0 ? MAX_RESULTS : _ref$request$maxResul;
      const _parseDomain = parseDomain(heroDomain),
        heroSld = _parseDomain.sld,
        heroTld = _parseDomain.tld;
      if (!heroSld) {
        return {
          alternatives: []
        };
      }
      if (isReuseRecommendationsOn()) {
        return listAlternativesFromRecommendations(httpClient, heroDomain, heroSld, maxResults);
      }
      const _DomainsApiClient$que2 = DomainsApiClient.queries({
          httpClient
        }),
        listTldPromotions = _DomainsApiClient$que2.listTldPromotions,
        checkDomainAvailability = _DomainsApiClient$que2.checkDomainAvailability,
        listDisplayPrices = _DomainsApiClient$que2.listDisplayPrices;
      const _await$listTldPromoti = await listTldPromotions({
          limit: TLD_POOL_LIMIT
        }),
        tldPromotions = _await$listTldPromoti.tldPromotions;
      const candidateDomains = (tldPromotions ?? []).map(promotion => promotion.tld).filter(tld => typeof tld === 'string' && tld.length > 0 && tld !== heroTld).map(tld => `${heroSld}.${tld}`);
      if (!candidateDomains.length) {
        return {
          alternatives: []
        };
      }
      const pages = Array.from({
        length: Math.ceil(candidateDomains.length / AVAILABILITY_PAGE_SIZE)
      }, (_, i) => candidateDomains.slice(i * AVAILABILITY_PAGE_SIZE, (i + 1) * AVAILABILITY_PAGE_SIZE));
      const stopThreshold = Math.min(MIN_RESULTS, maxResults);
      const availableDomains = [];
      for (const page of pages) {
        if (availableDomains.length >= stopThreshold) {
          break;
        }
        const results = await Promise.allSettled(page.map(domain => checkDomainAvailability({
          domain
        })));
        for (const _ref2 of results.entries()) {
          var _result$value;
          const idx = _ref2[0];
          const result = _ref2[1];
          if (result.status === 'fulfilled' && ((_result$value = result.value) == null || (_result$value = _result$value.availability) == null ? void 0 : _result$value.available) === true) {
            availableDomains.push({
              domain: page[idx],
              premium: result.value.availability.premium === true
            });
          }
        }
      }
      const finalAvailable = availableDomains.slice(0, maxResults);
      if (!finalAvailable.length) {
        return {
          alternatives: []
        };
      }
      const premiumByDomain = Object.fromEntries(finalAvailable.map(_ref3 => {
        let domain = _ref3.domain,
          premium = _ref3.premium;
        return [domain, premium];
      }));
      const buildAlternative = (domain, price) => ({
        id: domain,
        domain,
        premium: premiumByDomain[domain] === true,
        price
      });
      try {
        const _await$listDisplayPri = await listDisplayPrices({
            domains: finalAvailable.map(_ref4 => {
              let domain = _ref4.domain,
                premium = _ref4.premium;
              return {
                domain,
                premium
              };
            })
          }, {
            useErrorBoundary: false
          }),
          displayPrices = _await$listDisplayPri.displayPrices;
        const priceByDomain = Object.fromEntries((displayPrices ?? []).filter(price => typeof price.domain === 'string' && price.domain.length > 0).map(entry => {
          const domain = entry.domain,
            rest = _objectWithoutPropertiesLoose(entry, _excluded);
          return [domain, rest];
        }));
        return {
          alternatives: finalAvailable.map(_ref5 => {
            let domain = _ref5.domain;
            return buildAlternative(domain, priceByDomain[domain]);
          })
        };
      } catch {
        return {
          alternatives: finalAvailable.map(_ref6 => {
            let domain = _ref6.domain;
            return buildAlternative(domain);
          })
        };
      }
    },
    queryConfigType: QueryConfigTypes.STATIC_DATA
  }
});
export const tldAlternativesApiClient = createApiClient(apiConfig);
//# sourceMappingURL=TldAlternativesWithPrice.apiClient.js.map